import arrowUp from "../../assets/icons/arrow-up.svg";
import Image from "next/image";
import { motion } from "motion/react";
import { scrollToElement } from "@/lib/utils";

// Button component props definition
type ButtonProps = {
  text: string;
  scrollTo?: string; // ID of the element to scroll to
  onClick?: () => void;
};

const Button = ({ text, scrollTo, onClick }: ButtonProps) => {
  // Motion variants for the animated background circle
  const bgVariants = {
    // Initial scale before the hover animation
    initial: {
      scale: 0.2,
    },
    // Scale up to full size on hover
    hovered: {
      scale: 1,
    },
  };

  // Motion variants for the original text animation
  const textVariants = {
    // Start position when not hovered
    initial: {
      y: 0,
    },
    // Move text upward on hover for animation effect
    hovered: {
      y: "-150%",
    },
  };

  // Motion variants for the animated text that slides in
  const animatedTextVariants = {
    // Start from below the button
    initial: {
      y: "150%",
    },
    // Slide into place on hover
    hovered: {
      y: 0,
    },
  };

  const handleClick = () => {
    if (scrollTo) {
      scrollToElement(scrollTo);
    }
    if (onClick) {
      onClick();
    }
  };

  return (
    <>
      {/* Button container with styling and motion controls */}
      <motion.button
        className="bg-primary w-[180px] h-[56px] rounded-[32px] flex items-center justify-center cursor-pointer relative overflow-hidden text-nowrap z-[2] border-[1px] border-dashed border-black"
        // Set the initial animation state
        initial="initial"
        // Trigger "hovered" variants when hovered
        whileHover="hovered"
        // Slight scale reduction during tap interaction
        whileTap={{ scale: 0.95 }}
        onClick={handleClick}
      >
        {/* Contains the original button text and icon */}
        <motion.span
          className="font-editorial text-[24px] tracking-tight text-black z-10 flex mt-1 font-normal"
          // Apply the text motion variants
          variants={textVariants}
          // Configure the transition for a smooth effect
          transition={{
            duration: 0.5,
            ease: [0.87, 0, 0.13, 1], // Sharp acceleration and deceleration
          }}
        >
          {text}
          {/* Arrow icon displayed with the text */}
          <Image src={arrowUp} alt="arrow" className="mb-[1px]" />
        </motion.span>

        {/* Animated background circle */}
        <motion.div
          className="bg-accent absolute size-[300px] rounded-full -z-[1] -bottom-[180px]"
          // Apply background scaling variants
          variants={bgVariants}
          // Configure background transition properties
          transition={{
            duration: 0.5,
            ease: [0.87, 0, 0.13, 1], // Sharp acceleration and deceleration
          }}
        ></motion.div>

        {/* Animated text overlay that slides in */}
        <motion.span
          className="font-editorial text-[24px] tracking-tight text-black absolute mt-1 flex font-normal"
          // Apply animated text variants for the sliding effect
          variants={animatedTextVariants}
          // Transition details for the animated text
          transition={{
            duration: 0.5,
            ease: [0.87, 0, 0.13, 1], // Sharp acceleration and deceleration
          }}
        >
          {text}
          {/* Arrow icon repeated in animated layer */}
          <Image src={arrowUp} alt="arrow" className="mb-[1px]" />
        </motion.span>
      </motion.button>
    </>
  );
};

export default Button;
